import * as Popover from "@radix-ui/react-popover";
import { Bell, CheckCheck, CheckCircle2, Clock } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { cn } from "../../lib/cn";
import {
  type AccionPendiente,
  type Aviso,
  emitirAlertaDeOt,
  fetchCampanita,
  leerAviso,
  leerTodos,
  noSeFacturaOt,
  type PlazoPostergado,
  postergarAlerta,
} from "../../lib/consola-api";
import { hace } from "../../lib/tiempo";
import { useDatos } from "../../lib/useDatos";
import { esAvisoDeFacturacion } from "../../paginas/facturacion/acciones";
import { DialogoMotivo } from "../../paginas/facturacion/DialogoMotivo";
import { Boton } from "../ui/primitivos";

/**
 * La campanita (S9b, ADR 0030).
 *
 * **Dos bloques que se apagan distinto**, y por eso están separados: lo que
 * REQUIERE ACCIÓN no se va al mirarlo, y los AVISOS sí. Mezclarlos sería el
 * error que este slice existe para evitar.
 *
 * **No se actualiza sola.** Es la contracara deliberada de no usar el Durable
 * Object (ADR 0006): se resuelve al abrir el popover y después de cada acción.
 * Nada hace polling — un `setInterval` que refresca "por las dudas" rompe el
 * autosuspend de Neon, que es donde el sistema paga (invariante 11).
 *
 * `imprenta` tiene una campanita, pero es un **placeholder**: un botón con el
 * icono y sin `onClick`. No hay diseño que portear acá, así que sigue el
 * lenguaje visual del resto del dashboard (ADR 0059).
 */

const PLAZOS: { valor: PlazoPostergado; etiqueta: string }[] = [
  { valor: "manana", etiqueta: "Mañana" },
  { valor: "semana", etiqueta: "Una semana" },
];

/**
 * Una acción pendiente.
 *
 * Lleva una **barra ámbar a la izquierda** y no un ícono por fila: el color
 * corre a lo alto de toda la fila, así el bloque se lee como una columna de
 * cosas que esperan algo de vos aunque haya seis seguidas. Un ícono repetido
 * seis veces es ruido.
 */
function FilaAccion({
  accion,
  alDespachar,
  alPostergar,
  alEmitir,
  alNoFacturar,
}: {
  accion: AccionPendiente;
  alDespachar: (destino: string) => void;
  alPostergar: (plazo: PlazoPostergado) => void;
  /** Solo el aviso «OT cerrada sin alerta de facturación» (ADR 0099 A4). */
  alEmitir: () => void;
  alNoFacturar: () => void;
}) {
  const [eligiendoPlazo, setEligiendoPlazo] = useState(false);

  return (
    <li className="relative py-3 pr-3 pl-4 transition-colors hover:bg-neutral-50">
      <span
        aria-hidden="true"
        className="absolute top-3 bottom-3 left-0 w-0.5 rounded-r bg-status-pending"
      />
      <p className="font-medium text-neutral-900 text-sm">{accion.titulo}</p>
      {accion.detalle ? <p className="text-neutral-600 text-xs">{accion.detalle}</p> : null}
      <p className="mt-0.5 text-neutral-400 text-xs">pendiente {hace(accion.desde)}</p>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {accion.destino ? (
          <Boton variante="secundario" onClick={() => alDespachar(accion.destino as string)}>
            {accion.verbo}
          </Boton>
        ) : (
          // Deshabilitado CON el motivo a la vista: "no existe la sección" y "no
          // tenés permiso" son cosas distintas y no pueden verse igual
          // (ADR 0030, salvaguarda 4).
          <span className="flex flex-col">
            <Boton variante="secundario" disabled>
              {accion.verbo}
            </Boton>
            <span className="text-neutral-500 text-xs">{accion.motivoSinDestino}</span>
          </span>
        )}

        {/* Las dos decisiones del aviso, ahí mismo: emitir la alerta o decir
            por qué no se factura (ADR 0099 A4). */}
        {esAvisoDeFacturacion(accion.tipo) ? (
          <>
            <Boton variante="primario" onClick={alEmitir}>
              Emitir alerta
            </Boton>
            <Boton variante="secundario" onClick={alNoFacturar}>
              No se factura
            </Boton>
          </>
        ) : null}

        {eligiendoPlazo ? (
          <span className="flex items-center gap-1">
            {PLAZOS.map((p) => (
              <Boton
                key={p.valor}
                variante="fantasma"
                onClick={() => {
                  alPostergar(p.valor);
                  setEligiendoPlazo(false);
                }}
              >
                {p.etiqueta}
              </Boton>
            ))}
          </span>
        ) : (
          <Boton variante="fantasma" onClick={() => setEligiendoPlazo(true)}>
            <Clock className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
            Postergar
          </Boton>
        )}
      </div>
    </li>
  );
}

/**
 * Un aviso.
 *
 * El **punto azul** marca el no leído, en vez de pintarle el fondo a la fila:
 * con varios sin leer la lista quedaba a rayas, y lo que hay que distinguir es
 * cada ítem, no bloques. «Leído» aparece al pasar el mouse; sigue alcanzable
 * por teclado, pero en reposo no compite con el contenido.
 */
function FilaAviso({ aviso, alLeer }: { aviso: Aviso; alLeer: () => void }) {
  const sinLeer = aviso.leidaEn === null;

  return (
    <li
      className={cn(
        "group flex items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-neutral-50",
        sinLeer ? "" : "opacity-55",
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "mt-1.5 h-2 w-2 shrink-0 rounded-full",
          sinLeer ? "bg-brand-500" : "bg-transparent",
        )}
      />
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-neutral-900 text-sm",
            sinLeer ? "font-medium" : "font-normal",
          )}
        >
          {aviso.titulo}
        </p>
        {aviso.cuerpo ? <p className="truncate text-neutral-600 text-xs">{aviso.cuerpo}</p> : null}
        <p className="mt-0.5 text-neutral-400 text-xs">{hace(aviso.creadoEn)}</p>
      </div>
      {sinLeer ? (
        <Boton
          variante="fantasma"
          onClick={alLeer}
          aria-label={`Marcar leído: ${aviso.titulo}`}
          className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100"
        >
          Leído
        </Boton>
      ) : null}
    </li>
  );
}

/** Encabezado de sección: nombre, cuenta en pastilla y una acción opcional. */
function TituloDeSeccion({
  texto,
  cuenta,
  accion,
}: {
  texto: string;
  cuenta: number;
  accion?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-3 pt-3 pb-1.5">
      <span className="flex items-center gap-2">
        <span className="font-semibold text-[11px] text-neutral-500 uppercase tracking-wider">
          {texto}
        </span>
        {cuenta > 0 ? (
          <span className="rounded-full bg-neutral-100 px-1.5 font-medium text-[11px] text-neutral-600">
            {cuenta}
          </span>
        ) : null}
      </span>
      {accion}
    </div>
  );
}

export function Campanita({
  colapsado = false,
  lado = "bottom",
}: {
  /** En el sidebar colapsado queda solo el ícono; expandido, ícono y nombre. */
  colapsado?: boolean;
  /**
   * Hacia donde se abre. Lo decide quien la monta, no el componente: en el pie
   * del sidebar tiene que salir a la DERECHA para no tapar la navegacion, y en
   * la barra de mobile hacia abajo, que es el unico lugar donde hay espacio.
   */
  lado?: "bottom" | "right";
}) {
  const [abierto, setAbierto] = useState(false);
  const navegar = useNavigate();
  // La clave cambia al abrir, así que se refresca ahí y no antes: la campanita
  // cerrada no consulta nada.
  const [refresco, setRefresco] = useState(0);
  const datos = useDatos(fetchCampanita, String(refresco));

  const recargar = () => setRefresco((n) => n + 1);
  const [errorDeAccion, setErrorDeAccion] = useState<string | null>(null);
  // Fuera del popover: al abrir el diálogo el popover se cierra, y el estado no
  // puede vivir en una fila que se desmonta.
  const [sinFacturar, setSinFacturar] = useState<AccionPendiente | null>(null);

  const acciones = datos.datos?.acciones;
  const avisos = datos.datos?.avisos;
  // El badge suma las dos cosas, pero adentro van separadas.
  const total = (acciones?.total ?? 0) + (avisos?.sinLeer ?? 0);
  const recortadas = Object.values(acciones?.recortados ?? {}).reduce((a, b) => a + b, 0);
  const alDia = (acciones?.mostradas.length ?? 0) === 0 && (avisos?.items.length ?? 0) === 0;

  return (
    <>
      <Popover.Root
        open={abierto}
        onOpenChange={(v) => {
          setAbierto(v);
          if (v) recargar();
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={total > 0 ? `Notificaciones: ${total} sin resolver` : "Notificaciones"}
            className={cn(
              // Se lee como un ítem más de la navegación: mismo alto, mismo radio,
              // mismo hover. Antes era un ícono suelto que no se entendía como
              // parte del menú y no decía qué era.
              // El trigger vive en la sidebar oscura; el panel que abre es una
              // superficie clara, como el resto de los popovers del dashboard.
              "flex w-full items-center rounded-md py-2 font-medium text-sb-text text-sm transition-colors hover:bg-white/5 hover:text-white",
              colapsado ? "justify-center px-2" : "gap-3 px-3",
            )}
          >
            <span className="relative flex shrink-0 items-center">
              <Bell className="h-5 w-5" aria-hidden="true" />
              {/* Colapsado el número no entra al lado: vuelve al globo sobre el
                ícono, que es la única forma de mostrarlo en 64px de ancho. */}
              {colapsado && total > 0 ? (
                <span className="-top-1.5 -right-2 absolute flex h-4 min-w-4 items-center justify-center rounded-full bg-status-rejected px-1 font-semibold text-[10px] text-white">
                  {total > 9 ? "9+" : total}
                </span>
              ) : null}
            </span>

            {colapsado ? null : (
              <>
                <span className="flex-1 text-left">Notificaciones</span>
                {total > 0 ? (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-status-rejected px-1.5 font-semibold text-[11px] text-white">
                    {total > 99 ? "99+" : total}
                  </span>
                ) : null}
              </>
            )}
          </button>
        </Popover.Trigger>

        <Popover.Portal>
          <Popover.Content
            side={lado}
            align="end"
            collisionPadding={8}
            sideOffset={12}
            className="data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 z-50 w-[min(24rem,92vw)] overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-neutral-900/10 shadow-xl data-[state=open]:animate-in"
          >
            {/* La cabecera dice de una qué hay adentro, sin obligar a contar filas. */}
            <div className="border-neutral-100 border-b px-3 py-3">
              <p className="font-semibold text-base text-neutral-900">Notificaciones</p>
              <p className="text-neutral-500 text-xs">
                {datos.cargando && !datos.datos
                  ? "Buscando..."
                  : total > 0
                    ? `${total} sin resolver`
                    : "No hay nada pendiente"}
              </p>
            </div>

            {errorDeAccion ? (
              <p className="px-3 pt-3 text-red-700 text-sm">{errorDeAccion}</p>
            ) : null}

            {datos.error ? (
              <p className="px-3 py-6 text-center text-red-700 text-sm">{datos.error}</p>
            ) : null}

            {datos.datos ? (
              alDia ? (
                /* Estado vacío: lo dice una vez y se calla, en vez de dejar dos
                 encabezados con «Nada pendiente» debajo de cada uno. */
                <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                  <CheckCircle2 className="h-8 w-8 text-status-approved" aria-hidden="true" />
                  <p className="font-medium text-neutral-800 text-sm">Estás al día</p>
                  <p className="text-neutral-500 text-xs">
                    Cuando algo necesite tu atención va a aparecer acá.
                  </p>
                </div>
              ) : (
                <div className="max-h-[26rem] overflow-y-auto">
                  {(acciones?.mostradas.length ?? 0) > 0 ? (
                    <>
                      <TituloDeSeccion texto="Requieren acción" cuenta={acciones?.total ?? 0} />
                      <ul className="divide-y divide-neutral-100">
                        {acciones?.mostradas.map((accion) => (
                          <FilaAccion
                            key={`${accion.tipo}|${accion.id}`}
                            accion={accion}
                            alDespachar={(destino) => {
                              setAbierto(false);
                              navegar(destino);
                            }}
                            alPostergar={async (plazo) => {
                              await postergarAlerta(accion.tipo, accion.id, plazo);
                              recargar();
                            }}
                            alEmitir={async () => {
                              setErrorDeAccion(null);
                              try {
                                await emitirAlertaDeOt(accion.id);
                              } catch (err) {
                                setErrorDeAccion(
                                  err instanceof Error ? err.message : "La acción falló",
                                );
                              }
                              recargar();
                            }}
                            alNoFacturar={() => {
                              setAbierto(false);
                              setSinFacturar(accion);
                            }}
                          />
                        ))}
                      </ul>
                      {recortadas > 0 ? (
                        <p className="px-3 py-2 text-neutral-500 text-xs">
                          y {recortadas} más — abrí la sección para verlas todas
                        </p>
                      ) : null}
                    </>
                  ) : null}

                  {(avisos?.items.length ?? 0) > 0 ? (
                    <>
                      <TituloDeSeccion
                        texto="Avisos"
                        cuenta={avisos?.sinLeer ?? 0}
                        accion={
                          (avisos?.sinLeer ?? 0) > 0 ? (
                            <Boton
                              variante="fantasma"
                              onClick={async () => {
                                await leerTodos();
                                recargar();
                              }}
                            >
                              <CheckCheck className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
                              Marcar todos
                            </Boton>
                          ) : undefined
                        }
                      />
                      <ul className="divide-y divide-neutral-100">
                        {avisos?.items.map((aviso) => (
                          <FilaAviso
                            key={aviso.id}
                            aviso={aviso}
                            alLeer={async () => {
                              await leerAviso(aviso.id);
                              recargar();
                            }}
                          />
                        ))}
                      </ul>
                    </>
                  ) : null}
                </div>
              )
            ) : null}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>

      <DialogoMotivo
        abierto={sinFacturar !== null}
        titulo="No se factura"
        descripcion={`${sinFacturar?.titulo ?? ""}. El motivo queda registrado y el aviso se resuelve.`}
        confirmar="No se factura"
        onCerrar={() => setSinFacturar(null)}
        onConfirmar={async (motivo) => {
          if (!sinFacturar) return;
          await noSeFacturaOt(sinFacturar.id, motivo);
          recargar();
        }}
      />
    </>
  );
}
